import { createSlice } from '@reduxjs/toolkit';
import { authApi } from '../../api/endpoints/auth';

// Secure storage helper functions
// localStorage kullanıldı - yeni sekmede açıldığında auth state korunur
const secureStorage = {
  setItem: (key, value) => {
    localStorage.setItem(key, JSON.stringify(value));
  },
  getItem: (key) => {
    const item = localStorage.getItem(key);
    if (!item) return null;
    try {
      return JSON.parse(item);
    } catch (error) {
      return null;
    }
  },
  removeItem: (key) => {
    localStorage.removeItem(key);
  }
};

// Get auth data from localStorage
const getStoredAuth = () => {
  const auth = secureStorage.getItem('auth');
  return {
    token: auth?.token || null,
    refreshToken: auth?.refreshToken || null,
    user: auth?.user || null,
    csrfToken: auth?.csrfToken || null,
    // New auth details fields
    message: auth?.message || null,
    tokenExpiration: auth?.tokenExpiration || null,
    userId: auth?.userId || null,
    userName: auth?.userName || null,
    roles: auth?.roles || [],
    manageRoles: auth?.manageRoles || [],
    aftersales: auth?.aftersales || {
      isAftersalesUser: false,
      roles: []
    }
  };
};

const initialState = {
  ...getStoredAuth(),
  isAuthenticated: !!getStoredAuth().token,
  isLoading: false,
  error: null,
  forgotPasswordStatus: null,
  resetPasswordStatus: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, { payload }) => {
      state.token = payload.token;
      state.refreshToken = payload.refreshToken;
      state.user = payload.user;
      state.csrfToken = payload.csrfToken;
      state.isAuthenticated = true;
      state.error = null;

      // Save to session storage
      secureStorage.setItem('auth', {
        token: payload.token,
        refreshToken: payload.refreshToken,
        user: payload.user,
        csrfToken: payload.csrfToken,
      });
    },
    setAuthDetails: (state, { payload }) => {
      // Update all auth details from API response
      state.message = payload.message;
      state.csrfToken = payload.csrfToken;
      state.tokenExpiration = payload.tokenExpiration;
      state.userId = payload.userId;
      state.userName = payload.userName;
      state.roles = payload.roles || [];
      state.manageRoles = payload.manageRoles || [];
      state.aftersales = payload.aftersales || {
        isAftersalesUser: false,
        roles: []
      };
      state.isAuthenticated = payload.authenticated || false;
      state.error = null;

      // Save to session storage with new details
      const currentAuth = secureStorage.getItem('auth') || {};
      secureStorage.setItem('auth', {
        ...currentAuth,
        message: payload.message,
        csrfToken: payload.csrfToken,
        tokenExpiration: payload.tokenExpiration,
        userId: payload.userId,
        userName: payload.userName,
        roles: payload.roles,
        manageRoles: payload.manageRoles,
        aftersales: payload.aftersales,
        authenticated: payload.authenticated
      });
    },
    setUserRoles: (state, { payload }) => {
      state.roles = payload;
      
      // Update storage
      const currentAuth = secureStorage.getItem('auth') || {};
      secureStorage.setItem('auth', {
        ...currentAuth,
        roles: payload
      });
    },
    setAftersalesInfo: (state, { payload }) => {
      state.aftersales = { ...state.aftersales, ...payload };
      
      // Update storage
      const currentAuth = secureStorage.getItem('auth') || {};
      secureStorage.setItem('auth', {
        ...currentAuth,
        aftersales: state.aftersales
      });
    },
    refreshTokens: (state, { payload }) => {
      state.token = payload.token;
      if (payload.refreshToken) {
        state.refreshToken = payload.refreshToken;
      }
      if (payload.csrfToken) {
        state.csrfToken = payload.csrfToken;
      }

      // Update tokens
      const currentAuth = secureStorage.getItem('auth') || {};
      secureStorage.setItem('auth', {
        ...currentAuth,
        token: payload.token,
        refreshToken: payload.refreshToken || currentAuth.refreshToken,
        csrfToken: payload.csrfToken || currentAuth.csrfToken,
      });
    },
    logout: (state) => {
      state.token = null;
      state.refreshToken = null;
      state.user = null;
      state.csrfToken = null;
      state.isAuthenticated = false;
      state.error = null;
      // Clear new auth details
      state.message = null;
      state.tokenExpiration = null;
      state.userId = null;
      state.userName = null;
      state.roles = [];
      state.manageRoles = [];
      state.aftersales = {
        isAftersalesUser: false,
        roles: []
      };

      // Clear all storage - completely wipe sessionStorage and localStorage
      try {
        const savedLanguage = localStorage.getItem('selectedLanguage');
        const i18nextLng = localStorage.getItem('i18nextLng');

        // Send logout signal to other tabs BEFORE clearing storage
        // This ensures other tabs detect the logout event
        localStorage.setItem('logout-event', Date.now().toString());

        sessionStorage.clear();
        localStorage.clear();

        // Preserve language preference across logout
        if (savedLanguage) {
          localStorage.setItem('selectedLanguage', savedLanguage);
        }
        if (i18nextLng) {
          localStorage.setItem('i18nextLng', i18nextLng);
        }
      } catch (error) {
        console.error('Error clearing storage:', error);
      }
    },
    setLoading: (state, { payload }) => {
      state.isLoading = payload;
    },
    setError: (state, { payload }) => {
      state.error = payload;
      state.isLoading = false;
    },
    setForgotPasswordStatus: (state, { payload }) => {
      state.forgotPasswordStatus = payload;
    },
    clearForgotPasswordStatus: (state) => {
      state.forgotPasswordStatus = null;
    },
    setResetPasswordStatus: (state, { payload }) => {
      state.resetPasswordStatus = payload;
    },
    clearResetPasswordStatus: (state) => {
      state.resetPasswordStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle login mutation results
      .addMatcher(
        authApi.endpoints.login.matchFulfilled,
        (state, { payload }) => {
          state.token = payload.token;
          state.refreshToken = payload.refreshToken;
          state.user = payload.user;
          state.isAuthenticated = payload.authenticated || true;
          state.isLoading = false;
          state.error = null;
          state.csrfToken = payload.csrfToken; // Store CSRF token for later use

          secureStorage.setItem('auth', {
            token: payload.token,
            refreshToken: payload.refreshToken,
            user: payload.user,
            csrfToken: payload.csrfToken,
            authenticated: payload.authenticated
          });
        }
      )
      .addMatcher(
        authApi.endpoints.login.matchRejected,
        (state, { payload }) => {
          state.isLoading = false;
          state.error =
            payload?.data?.errorMessage ||
            payload?.data?.message ||
            'Giriş başarısız oldu';
        }
      )
      .addMatcher(
        authApi.endpoints.forgotPassword.matchFulfilled,
        (state) => {
          state.forgotPasswordStatus = 'success';
          state.isLoading = false;
        }
      )
      .addMatcher(
        authApi.endpoints.forgotPassword.matchRejected,
        (state, { payload }) => {
          state.forgotPasswordStatus = 'error';
          state.error = payload?.data?.message || 'Şifre sıfırlama işlemi başarısız oldu';
          state.isLoading = false;
        }
      )
      .addMatcher(
        authApi.endpoints.resetPassword.matchFulfilled,
        (state) => {
          state.resetPasswordStatus = 'success';
          state.isLoading = false;
        }
      )
      .addMatcher(
        authApi.endpoints.resetPassword.matchRejected,
        (state, { payload }) => {
          state.resetPasswordStatus = 'error';
          state.error = payload?.data?.message || 'Şifre sıfırlama işlemi başarısız oldu';
          state.isLoading = false;
        }
      );
  },
});

export const {
  setCredentials,
  setAuthDetails,
  setUserRoles,
  setAftersalesInfo,
  refreshTokens,
  logout,
  setLoading,
  setError,
  setForgotPasswordStatus,
  clearForgotPasswordStatus,
  setResetPasswordStatus,
  clearResetPasswordStatus,
} = authSlice.actions;

// Selectors
export const selectCurrentUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthLoading = (state) => state.auth.isLoading;
export const selectAuthError = (state) => state.auth.error;
export const selectForgotPasswordStatus = (state) => state.auth.forgotPasswordStatus;
export const selectResetPasswordStatus = (state) => state.auth.resetPasswordStatus;
export const selectCsrfToken = (state) => state.auth.csrfToken;

// New auth details selectors
export const selectAuthDetails = (state) => state.auth;
export const selectUserId = (state) => state.auth.userId;
export const selectUserName = (state) => state.auth.userName;
export const selectUserRoles = (state) => state.auth.roles;
export const selectRoles = (state) => state.auth.roles; // Alias for selectUserRoles
export const selectManageRoles = (state) => state.auth.manageRoles || [];
export const selectTokenExpiration = (state) => state.auth.tokenExpiration;
export const selectAftersalesInfo = (state) => state.auth.aftersales;
export const selectIsAftersalesUser = (state) => state.auth.aftersales?.isAftersalesUser || false;
export const selectAftersalesRoles = (state) => state.auth.aftersales?.roles || [];

// Helper selectors
export const selectHasRole = (role) => (state) => 
    state.auth.roles?.includes(role) || false;

export const selectHasAnyRole = (roles) => (state) => 
    roles.some(role => state.auth.roles?.includes(role)) || false;

export const selectHasAftersalesRole = (role) => (state) => 
    state.auth.aftersales?.roles?.includes(role) || false;

// Token validation selectors
export const selectIsTokenExpired = (state) => {
    const expiration = state.auth.tokenExpiration;
    if (!expiration) return false;
    return new Date(expiration) <= new Date();
};

export const selectIsValidSession = (state) => {
    const auth = state.auth;
    return auth.isAuthenticated && 
           auth.userId && 
           !selectIsTokenExpired(state);
};

export default authSlice.reducer;
